import {
  Popover,
  PopoverDisclosure,
  PopoverProvider
} from '@ariakit/react/popover';
import { domAnimation, motion } from 'framer-motion';
import { useState } from 'react';
import type { Menu, MenuItem, SubMenuItem } from 'types/capi/plp';

import ChevronIcon from '~/assets/icons/Chevron';
import FramerMotion from '~/components/FramerMotion';
import Tracked from '~/components/Tracked';
import type { TrackedButtonClickName } from '~/services/gtm.client';
import gtm from '~/services/gtm.client';

import FilterOptionsList from './FilterOptionsList';
import SizeFilterGrid from './SizeFilterGrid';

type Props = {
  menus: Menu[];
};

function getSizeTitle(active: boolean, title: string, text?: string) {
  if (!active) return title;

  return `UK ${text}`;
}

function getTitle(active: boolean, title: string, text?: string) {
  if (!active) return title === 'Discover' ? 'Gender' : title;

  return text;
}

function getEventName(title: string) {
  return title === 'Discover' ? 'Gender' : title;
}

const Dropdown = ({
  items,
  title,
  activeItem,
  menuId
}: {
  activeItem?: MenuItem;
  items: (MenuItem | SubMenuItem)[];
  menuId: string;
  title: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isSizeFilter = menuId === 'SIZE_MENU';
  const stylingForFilterType = isSizeFilter
    ? 'min-w-fit'
    : 'min-w-[250px] max-h-96 overflow-auto';

  function onOptionSelect({
    filterName,
    filterValue
  }: {
    filterName: string;
    filterValue: string;
  }) {
    setIsOpen(false);

    gtm.sendPlpProductFilterEvent({
      filterName,
      filterValue,
      pagePosition: 'Filter Toolbar'
    });
  }

  return (
    <PopoverProvider open={isOpen}>
      <PopoverDisclosure onClick={() => setIsOpen(!isOpen)}>
        <Tracked.Button
          variant="none"
          pagePosition="Top Bar"
          name={getEventName(title) as TrackedButtonClickName}
          className={`flex h-8 items-center gap-2 ${activeItem ? 'bg-black' : isSizeFilter ? 'bg-forest' : 'bg-white'} rounded-lg border border-grey-divide px-4`}
        >
          <p
            className={`whitespace-nowrap ${activeItem ? 'text-white' : isSizeFilter ? 'text-white' : 'text-grey-hide'} text-xs leading-5`}
          >
            {isSizeFilter
              ? getSizeTitle(Boolean(activeItem), title, activeItem?.text)
              : getTitle(Boolean(activeItem), title, activeItem?.text)}
          </p>
          <div className={isOpen ? 'rotate-180' : ''}>
            <ChevronIcon stroke="#A9A9A9" width="10" height="10" />{' '}
          </div>
        </Tracked.Button>
      </PopoverDisclosure>
      <FramerMotion feature={domAnimation}>
        <Popover
          render={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
          }
          onClose={() => setIsOpen(false)}
          className={`z-40 w-2/3 max-w-lg rounded-md bg-white shadow-lg ${stylingForFilterType}`}
        >
          {isSizeFilter ? (
            <div className="p-4">
              <SizeFilterGrid items={items} onOptionSelect={onOptionSelect} />
            </div>
          ) : (
            <FilterOptionsList
              menuId={menuId}
              onOptionSelect={onOptionSelect}
              title={title}
              isDropdown
              items={items}
            />
          )}
        </Popover>
      </FramerMotion>
    </PopoverProvider>
  );
};

const Filter = ({ menu }: { menu: Menu }) => {
  const activeMenu = menu.items.find((i) => menu.active && i.active);

  return (
    <>
      <Dropdown
        menuId={menu.id}
        title={menu.title}
        items={menu.items}
        activeItem={activeMenu}
      />
      {activeMenu?.sub_menus?.map((subMenu) => (
        <Dropdown
          menuId={subMenu.id}
          activeItem={subMenu.items.find((i) => subMenu.active && i.active)}
          key={subMenu.id}
          title={subMenu.title}
          items={subMenu.items}
        />
      ))}
    </>
  );
};

const Filters = ({ menus }: Props) => {
  return menus.map((menu) => (
    <Filter key={`${menu.title}-filter`} menu={menu} />
  ));
};

export default Filters;
